import { Box, Dialog, DialogTitle, DialogContent, TextField, DialogActions, MenuItem } from '@mui/material';
import { useState, useEffect, useRef } from "react";
import { useTableViewManager } from "src/core/tables/useTableViews";
import CustomButton from 'src/core/ui/CustomButton';
import { ReviewTextField } from 'src/core/forms/FieldReview';

export function TableViewDropdown({ disabled = false, activeViewId, viewsList, handleViewChange, label = "Select View", fullWidth = true, size = "small", onSelected, }: TableViewDropdownProps) {
  const [open, setOpen] = useState(false);
  const value = activeViewId ?? "";
  const selectRef = useRef<HTMLDivElement | null>(null);
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  // This grid popup needs its input ref, portaled menu and click-away/propagation handlers.
  return (
    <TextField inputRef={selectRef} label={label} select fullWidth={fullWidth} size={size} value={value} onChange={(e) => {
      e.stopPropagation();
      const id = e.target.value as View["id"];
      handleViewChange(id);
      setOpen(false);
      onSelected?.(id); // close parent column menu explicitly
    }}
      // prevent the trigger click from being treated as click-away
      onMouseDown={stop} onClick={stop} SelectProps={{
        open,
        onOpen: (e) => {
          e.stopPropagation();
          setOpen(true);
        },
        onClose: (e) => {
          e?.stopPropagation?.();
          setOpen(false);
        },
        // IMPORTANT: keep portal ON for correct positioning inside transformed poppers
        MenuProps: {
          disablePortal: false,       // <-- use portal to fix "middle of screen"
          disableScrollLock: true,
          // stop the Grid's click-away at the portaled paper/list
          MenuListProps: { onMouseDown: stop, onClick: stop, },
          PaperProps: { onMouseDown: stop, onClick: stop, },
        },
      }} placeholder={viewsList.length ? undefined : "No views"} disabled={disabled || !viewsList.length}>
      {viewsList.map((v) => (<MenuItem key={v.id} value={v.id} onMouseDown={stop} onClick={stop}>{v.name}</MenuItem>))}
    </TextField>
  );
}

/* ──────────────────────────────────────────────────────────────────
   COMPONENTS
─────────────────────────────────────────────────────────────────── */
export const ViewNameDialog = ({ open, onClose, onSubmit, title, initialValue = "", submitLabel = "Save", }) => {
  const [viewName, setViewName] = useState(initialValue);
  useEffect(() => {
    if (open) setViewName(initialValue);
  }, [open, initialValue]);
  const handleSubmit = () => {
    if (viewName.trim()) {
      onSubmit(viewName.trim());
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {/* Preserve initial dialog focus; ordinary CustomField does not forward autoFocus. */}
        <ReviewTextField autoFocus margin="dense" label="View Name" fullWidth value={viewName} onChange={e => setViewName(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Box sx={{ width: 'fit-content' }}><CustomButton title="Cancel" variant="text" onClick={onClose} /></Box>
        <Box sx={{ width: 'fit-content' }}><CustomButton title={submitLabel} onClick={handleSubmit} /></Box>
      </DialogActions>
    </Dialog>
  );
};

export const TableViewSelector = ({ tableSettings, syncGrid = false, onSelected, }: { tableSettings: any; syncGrid?: boolean; onSelected?: (id: string | number) => void; }) => {
  const { activeViewId, viewsList, handleViewChange, canEdit } = useTableViewManager(tableSettings, { syncGrid });

  return (
    <TableViewDropdown activeViewId={activeViewId} viewsList={viewsList} handleViewChange={handleViewChange} onSelected={onSelected} disabled={!canEdit} />
  );
};

type View = { id: string | number; name: string };

type TableViewDropdownProps = {
  disabled?: boolean;
  activeViewId: View["id"] | "" | undefined;
  viewsList: View[];
  handleViewChange: (id: View["id"]) => void;
  label?: string;
  fullWidth?: boolean;
  size?: "small" | "medium";
  /** call to close the Grid column menu after selection */
  onSelected?: (id: View["id"]) => void;
};
